import React, { useState } from 'react'

function AgencyDistribution({ clients }) {
  const [viewMode, setViewMode] = useState('clients') // 'clients' or 'budget'
  
  // Get agency data
  const getAgencyData = () => {
    const agencies = {}
    
    clients.forEach(client => {
      const agency = client.agency || 'No Agency'
      if (!agencies[agency]) {
        agencies[agency] = {
          name: agency,
          clients: 0,
          activeClients: 0,
          budget: 0
        }
      }
      agencies[agency].clients++
      if (client.status === 'Active') {
        agencies[agency].activeClients++
      }
      agencies[agency].budget += (client.linear_budget || 0) + (client.streaming_budget || 0)
    })
    
    return Object.values(agencies).sort((a, b) => {
      if (viewMode === 'budget') return b.budget - a.budget
      return b.clients - a.clients
    })
  }

  const agencyData = getAgencyData()
  const maxValue = viewMode === 'budget' 
    ? Math.max(...agencyData.map(a => a.budget))
    : Math.max(...agencyData.map(a => a.clients))

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`
    }
    return `$${amount}`
  }

  const formatFullCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0)
  }

  const getBarColor = (index) => {
    const colors = ['#eab308', '#111827', '#6b7280', '#d1d5db', '#fef3c7', '#1f2937', '#9ca3af', '#f3f4f6']
    return colors[index % colors.length]
  }

  const totalBudget = agencyData.reduce((sum, a) => sum + a.budget, 0)
  const totalClients = agencyData.reduce((sum, a) => sum + a.clients, 0)

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      padding: '24px'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#111827',
          margin: 0
        }}>
          Clients by Agency
        </h3>
        
        <div style={{ display: 'flex', gap: '4px' }}>
          <button
            onClick={() => setViewMode('clients')}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: '500',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              backgroundColor: viewMode === 'clients' ? '#111827' : '#f3f4f6',
              color: viewMode === 'clients' ? 'white' : '#6b7280'
            }}
          >
            By Count
          </button>
          <button
            onClick={() => setViewMode('budget')}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: '500',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              backgroundColor: viewMode === 'budget' ? '#111827' : '#f3f4f6',
              color: viewMode === 'budget' ? 'white' : '#6b7280'
            }}
          >
            By Budget
          </button>
        </div>
      </div>

      {/* Summary row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px',
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px'
      }}>
        <div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Agencies</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>{agencyData.length}</div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Clients</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>{totalClients}</div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Budget</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>{formatCurrency(totalBudget)}</div>
        </div>
      </div>

      {/* Bar chart */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {agencyData.map((agency, index) => {
          const value = viewMode === 'budget' ? agency.budget : agency.clients
          const barWidth = maxValue > 0 ? (value / maxValue) * 100 : 0
          
          return (
            <div key={agency.name}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '4px'
              }}>
                <div style={{ 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {agency.name}
                  <span style={{ 
                    fontSize: '11px', 
                    color: '#9ca3af',
                    fontWeight: '400'
                  }}>
                    ({agency.activeClients} active)
                  </span>
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#111827',
                  fontFamily: viewMode === 'budget' ? 'monospace' : 'inherit'
                }}>
                  {viewMode === 'budget' ? formatFullCurrency(agency.budget) : `${agency.clients} clients`}
                </div>
              </div>
              <div style={{
                height: '24px',
                backgroundColor: '#f3f4f6',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${barWidth}%`,
                  backgroundColor: getBarColor(index),
                  borderRadius: '4px',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          )
        })}
      </div>

      {/* Agency detail table */}
      <div style={{ marginTop: '24px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <th style={{ padding: '8px 0', textAlign: 'left', fontWeight: '600', color: '#6b7280' }}>Agency</th>
              <th style={{ padding: '8px 0', textAlign: 'right', fontWeight: '600', color: '#6b7280' }}>Clients</th>
              <th style={{ padding: '8px 0', textAlign: 'right', fontWeight: '600', color: '#6b7280' }}>Active</th>
              <th style={{ padding: '8px 0', textAlign: 'right', fontWeight: '600', color: '#6b7280' }}>Budget</th>
              <th style={{ padding: '8px 0', textAlign: 'right', fontWeight: '600', color: '#6b7280' }}>% of Total</th>
            </tr>
          </thead>
          <tbody>
            {agencyData.map((agency, idx) => (
              <tr key={agency.name} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '10px 0', fontWeight: '500', color: '#111827' }}>{agency.name}</td>
                <td style={{ padding: '10px 0', textAlign: 'right', color: '#374151' }}>{agency.clients}</td>
                <td style={{ padding: '10px 0', textAlign: 'right', color: '#374151' }}>{agency.activeClients}</td>
                <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: 'monospace', color: '#374151' }}>
                  {formatCurrency(agency.budget)}
                </td>
                <td style={{ padding: '10px 0', textAlign: 'right', color: '#6b7280' }}>
                  {totalBudget > 0 ? `${((agency.budget / totalBudget) * 100).toFixed(1)}%` : '0%'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default AgencyDistribution